import { BUSINESS_PROFILE, RAZORPAY_APPLICATION_DETAILS } from './businessProfile';

// Policy bodies ported from the public marketing site (marketing-site/app/*/page.tsx)
// so the merchant portal and the Razorpay-reviewed marketing site stay in lockstep.
// Update both surfaces together if any policy text changes.

const mailto = () => `mailto:${BUSINESS_PROFILE.supportEmail}`;

const ContactBlock = () => (
  <p>
    <strong>
      {BUSINESS_PROFILE.proprietorName} ({BUSINESS_PROFILE.legalEntityLabel})
    </strong>
    <br />
    {BUSINESS_PROFILE.registeredAddress}
    <br />
    {BUSINESS_PROFILE.cityAndState}, {BUSINESS_PROFILE.country}
    <br />
    Email: <a href={mailto()}>{BUSINESS_PROFILE.supportEmail}</a>
    <br />
    Phone: {BUSINESS_PROFILE.supportPhone}
  </p>
);

const PrivacyBody = () => (
  <>
    <p>
      This Privacy Policy explains how{' '}
      <strong>
        {BUSINESS_PROFILE.proprietorName} ({BUSINESS_PROFILE.legalEntityLabel})
      </strong>{' '}
      (&ldquo;Zopeo&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) collects, uses, discloses, retains,
      and protects personal information in connection with the Zopeo website, merchant dashboard, mobile applications,
      support channels, and related services (collectively, the &ldquo;Services&rdquo;).
    </p>
    <p>
      Zopeo is a business-to-business software platform for merchants. Merchants use Zopeo to manage appointments,
      customer engagement, offers, memberships, analytics, and billing workflows. This Policy applies to merchants,
      merchant staff, prospective customers, website visitors, and end customers whose data is processed through the
      Services on behalf of a merchant.
    </p>

    <h2>1. Our Role</h2>
    <p>
      For merchant account, billing, support, marketing, and website data, Zopeo generally acts as an independent data
      controller. For end-customer data entered into the Services by a merchant, Zopeo generally acts as a data
      processor or service provider and processes that information according to the merchant&rsquo;s instructions and
      applicable law.
    </p>

    <h2>2. Information We Collect</h2>
    <ul>
      <li>
        <strong>Merchant Account Information:</strong> Business name, proprietor or authorised contact name, email
        address, phone number, business address, store details, staff details, login credentials, and account
        preferences.
      </li>
      <li>
        <strong>End-Customer Information:</strong> Customer name, phone number, appointment or queue details, membership
        activity, offer activity, service selections, and visit-related records that merchants manage through Zopeo.
      </li>
      <li>
        <strong>Payment and Billing Information:</strong> Plan selection, invoices, subscription or usage charges,
        payment status, refund status, and transaction identifiers. Payment method details such as card numbers, UPI
        handles, netbanking credentials, and CVV are collected and processed by Razorpay or other authorised payment
        partners. Zopeo does not store full card numbers or banking credentials.
      </li>
      <li>
        <strong>Technical and Usage Information:</strong> IP address, device identifiers, browser type, operating system,
        app version, log data, session activity, feature usage, diagnostics, crash reports, and approximate location
        inferred from network or device settings where applicable.
      </li>
      <li>
        <strong>Communications:</strong> Messages, support tickets, email correspondence, demo requests, feedback, and
        call or chat metadata when you contact us.
      </li>
    </ul>

    <h2>3. How We Use Information</h2>
    <ul>
      <li>To provide, operate, maintain, secure, and improve the Services.</li>
      <li>To create and administer merchant accounts, staff access, plans, invoices, and usage billing.</li>
      <li>
        To send transactional messages such as OTPs, booking notifications, reminders, invoices, payment updates, and
        support responses.
      </li>
      <li>
        To process payments, reconcile transactions, detect billing errors, and coordinate approved refunds through our
        payment partners.
      </li>
      <li>To monitor reliability, debug issues, prevent misuse, detect fraud, and enforce our Terms.</li>
      <li>To respond to enquiries, provide onboarding, and communicate important service or policy updates.</li>
      <li>To comply with legal, tax, accounting, security, dispute-resolution, and regulatory obligations.</li>
    </ul>

    <h2>4. Legal Basis and Consent</h2>
    <p>
      We process information where it is necessary to perform a contract, operate our legitimate business interests,
      comply with legal obligations, protect users and the Services, or where consent is required by applicable law.
      Merchants are responsible for obtaining any notices, permissions, and consents required from their own customers
      and staff before using Zopeo to process their information.
    </p>

    <h2>5. Sharing and Disclosure</h2>
    <p>We do not sell personal information. We disclose information only where reasonably required, including to:</p>
    <ul>
      <li>
        <strong>Payment Partners:</strong> Razorpay and banking/payment infrastructure providers for payment
        collection, reconciliation, and refunds.
      </li>
      <li>
        <strong>Communication Providers:</strong> SMS, WhatsApp, email, push notification, and support tools used to
        deliver service messages.
      </li>
      <li>
        <strong>Cloud and Technology Providers:</strong> Hosting, storage, analytics, logging, monitoring, security, and
        infrastructure vendors.
      </li>
      <li>
        <strong>Professional Advisers:</strong> Legal, accounting, tax, compliance, and audit advisers where required.
      </li>
      <li>
        <strong>Authorities:</strong> Law enforcement, courts, regulators, or government bodies where disclosure is
        required by law or necessary to protect rights, users, and the Services.
      </li>
    </ul>

    <h2>6. Data Security</h2>
    <p>
      We use administrative, technical, and organisational safeguards designed to protect information, including
      encryption in transit, access controls, credential protection, logging, monitoring, and restricted access based on
      business need. No system is completely secure, but we work to maintain controls appropriate to the nature of the
      information we process.
    </p>

    <h2>7. Retention</h2>
    <p>
      We retain information for as long as necessary to provide the Services, maintain business records, resolve
      disputes, enforce agreements, meet tax and accounting requirements, and comply with applicable law. When
      information is no longer required, we delete, anonymise, or securely retain it only for permitted archival, backup,
      legal, or compliance purposes.
    </p>

    <h2>8. Your Choices and Rights</h2>
    <p>
      Subject to applicable law, you may request access to, correction of, deletion of, or restriction of processing of
      your personal information. End customers should first contact the merchant with whom they interacted, because the
      merchant controls most end-customer records in Zopeo. We will assist merchants with valid privacy requests where
      required.
    </p>

    <h2>9. Cookies and Analytics</h2>
    <p>
      We may use cookies, local storage, and similar technologies to keep users signed in, protect accounts, remember
      preferences, measure performance, and improve the website and Services. You can control cookies through your
      browser settings, but disabling them may affect core functionality.
    </p>

    <h2>10. Children</h2>
    <p>
      The Services are intended for businesses and are not directed to children under 18. We do not knowingly collect
      personal information directly from children. Merchants are responsible for ensuring they have a lawful basis to
      process any customer information they enter into the Services.
    </p>

    <h2>11. Changes to This Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. The revised version will be posted on this page with an
      updated &ldquo;Last updated&rdquo; date. Material changes may also be communicated through the Services or by email.
    </p>

    <h2>12. Contact Us</h2>
    <ContactBlock />
  </>
);

const TermsBody = () => (
  <>
    <p>
      These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern access to and use of the Zopeo website, merchant
      dashboard, mobile applications, support services, and related software features (collectively, the
      &ldquo;Services&rdquo;) operated by{' '}
      <strong>
        {BUSINESS_PROFILE.proprietorName} ({BUSINESS_PROFILE.legalEntityLabel})
      </strong>{' '}
      (&ldquo;Zopeo&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;).
    </p>
    <p>
      By creating an account, accessing the Services, submitting a demo request, or making a payment, you confirm that
      you have read, understood, and agreed to these Terms on behalf of yourself or the business you represent.
    </p>

    <h2>1. Business-to-business services</h2>
    <p>
      Zopeo provides software tools for merchants to manage appointments, customer engagement, offers, memberships,
      analytics, merchant workflows, and billing-related operations. Zopeo is not a bank, wallet, escrow provider,
      payment aggregator, or financial services provider. Merchant payments to Zopeo are collected through authorised
      payment partners such as Razorpay.
    </p>

    <h2>2. Eligibility and authority</h2>
    <ul>
      <li>You must be at least 18 years old to use the Services.</li>
      <li>
        If you use the Services for a business, you represent that you are authorised to bind that business to these
        Terms.
      </li>
      <li>You must provide accurate, complete, and current account, business, billing, and contact information.</li>
      <li>
        You are responsible for maintaining the confidentiality of account credentials and for all activity under your
        account.
      </li>
    </ul>

    <h2>3. Account onboarding and verification</h2>
    <p>
      We may request information needed to onboard, support, bill, or verify a merchant account. Access to certain
      features may depend on verification, plan status, payment confirmation, technical readiness, or compliance review.
      We may refuse, suspend, or limit access where account information is incomplete, inaccurate, unlawful, high-risk,
      or inconsistent with these Terms.
    </p>

    <h2>4. Fees, plans, and billing</h2>
    <ul>
      <li>
        <strong>Subscriptions:</strong> Merchants may subscribe to paid plans billed on a recurring basis. Plan
        features, limits, and prices are shown on the pricing page, dashboard, invoice, or commercial communication
        provided at the time of purchase.
      </li>
      <li>
        <strong>Usage fees:</strong> Zopeo may charge usage-based fees, including per-confirmed-booking fees. A booking
        is considered confirmed when the customer visit is recorded or verified through the applicable Zopeo workflow.
      </li>
      <li>
        <strong>Merchant-only charges:</strong> Zopeo charges merchants for software access and usage. Zopeo does not
        charge end customers directly for merchant services.
      </li>
      <li>
        <strong>Taxes:</strong> Prices may be exclusive of applicable taxes unless expressly stated otherwise. The
        merchant is responsible for taxes, duties, or levies applicable to its use of the Services.
      </li>
      <li>
        <strong>Payment processing:</strong> Payments are processed by Razorpay or another authorised payment partner. By
        paying through such provider, you may also be subject to that provider&rsquo;s terms and policies.
      </li>
    </ul>

    <h2>5. Cancellations and refunds</h2>
    <p>
      Cancellations, billing disputes, and refunds are governed by our Cancellation &amp; Refund Policy. Approved refunds
      are processed back to the original payment method through Razorpay or the applicable payment partner. Payment
      gateway processing timelines may vary by bank, payment method, and provider.
    </p>

    <h2>6. Merchant responsibilities</h2>
    <ul>
      <li>Use the Services only for lawful business purposes.</li>
      <li>Maintain accurate customer, booking, membership, offer, staff, catalogue, and billing records.</li>
      <li>Obtain all notices, consents, and permissions required to process customer and staff data through Zopeo.</li>
      <li>
        Comply with applicable laws, consumer protection obligations, privacy requirements, tax requirements, and
        industry rules.
      </li>
      <li>Ensure that merchant staff use the Services responsibly and with appropriate authorisation.</li>
      <li>Not use Zopeo to misrepresent bookings, inflate usage, create false transactions, abuse offers, or commit fraud.</li>
    </ul>

    <h2>7. Prohibited use</h2>
    <p>You must not:</p>
    <ul>
      <li>
        Reverse-engineer, copy, modify, resell, sublicense, or commercially exploit the Services except as expressly
        permitted.
      </li>
      <li>Interfere with security, availability, integrity, rate limits, authentication, or infrastructure of the Services.</li>
      <li>Upload unlawful, harmful, infringing, misleading, abusive, or malicious content.</li>
      <li>Use the Services for regulated, restricted, or high-risk activities without our prior written approval.</li>
      <li>Attempt unauthorised access to any account, system, data, API, or network.</li>
    </ul>

    <h2>8. Data and privacy</h2>
    <p>
      Our Privacy Policy explains how we collect, use, store, and protect information. Merchants retain responsibility
      for the legality, accuracy, and quality of the business and customer data they submit to the Services. We may
      process account, usage, and operational data to provide and improve the Services, prevent abuse, and comply with
      legal obligations.
    </p>

    <h2>9. Intellectual property</h2>
    <p>
      The Services, software, design, workflows, documentation, trademarks, logos, and other Zopeo materials are owned by
      Zopeo or its licensors. Subject to these Terms, we grant you a limited, revocable, non-exclusive, non-transferable
      right to use the Services for your internal business operations. No ownership rights are transferred to you.
    </p>

    <h2>10. Third-party services</h2>
    <p>
      The Services may integrate with or rely on third-party providers, including payment gateways, SMS/email providers,
      hosting providers, analytics tools, and app stores. We are not responsible for third-party services, outages,
      delays, policy changes, or processing failures outside our reasonable control.
    </p>

    <h2>11. Availability and changes</h2>
    <p>
      We aim to provide reliable Services, but we do not guarantee uninterrupted or error-free operation. We may modify,
      improve, suspend, or discontinue features from time to time, including for maintenance, security, compliance,
      business, or product reasons. Where practical, we will provide notice of material changes affecting paid accounts.
    </p>

    <h2>12. Suspension and termination</h2>
    <p>
      You may stop using the Services at any time. We may suspend or terminate access if you breach these Terms, fail to
      pay amounts due, create security or legal risk, misuse the Services, provide inaccurate information, or where
      required by law or payment partner policy. Termination does not waive amounts already due.
    </p>

    <h2>13. Disclaimers</h2>
    <p>
      To the maximum extent permitted by law, the Services are provided on an &ldquo;as is&rdquo; and &ldquo;as
      available&rdquo; basis. We disclaim all implied warranties, including warranties of merchantability, fitness for a
      particular purpose, non-infringement, accuracy, and uninterrupted availability.
    </p>

    <h2>14. Limitation of liability</h2>
    <p>
      To the maximum extent permitted by law, Zopeo will not be liable for indirect, incidental, special, consequential,
      exemplary, or punitive damages, or for loss of profits, revenue, goodwill, data, or business opportunity. Our
      aggregate liability arising out of or relating to the Services will not exceed the fees paid by you to Zopeo in the
      three months immediately preceding the event giving rise to the claim.
    </p>

    <h2>15. Indemnity</h2>
    <p>
      You agree to indemnify and hold Zopeo harmless from claims, losses, liabilities, damages, penalties, costs, and
      expenses arising from your use of the Services, your business operations, your data, your breach of these Terms, or
      your violation of applicable law or third-party rights.
    </p>

    <h2>16. Governing law and jurisdiction</h2>
    <p>
      These Terms are governed by the laws of India. Subject to applicable law, the courts at{' '}
      <strong>Hyderabad, Telangana</strong> will have exclusive jurisdiction over disputes arising from or relating to
      these Terms or the Services.
    </p>

    <h2>17. Updates to these Terms</h2>
    <p>
      We may update these Terms from time to time. Updated Terms will be posted on this page with a revised &ldquo;Last
      updated&rdquo; date. Continued use of the Services after the effective date of updated Terms constitutes acceptance.
    </p>

    <h2>18. Contact</h2>
    <ContactBlock />
  </>
);

const RefundBody = () => (
  <>
    <p>
      This policy explains how cancellations and refunds work for paid Zopeo Services. It applies to Merchants who pay
      for a subscription and/or per-confirmed-booking usage fees. <strong>Zopeo does not charge end customers</strong>,
      so this policy concerns Merchant billing only.
    </p>

    <h2>1. Subscription cancellation</h2>
    <ul>
      <li>You may cancel your subscription at any time from the merchant dashboard or by emailing us.</li>
      <li>
        Cancellation takes effect at the end of your current billing cycle. You keep access to paid features until that
        date.
      </li>
      <li>
        Subscription fees are billed for the cycle in advance and are <strong>generally non-refundable</strong> for the
        remaining portion of a cycle, except where required by law or in the case of a billing error (see &sect;3).
      </li>
    </ul>

    <h2>2. Per-confirmed-booking usage fees</h2>
    <ul>
      <li>
        Usage fees accrue only on <strong>confirmed bookings</strong> (customer arrived and verified) and are invoiced
        monthly in arrears.
      </li>
      <li>
        Once accrued for a genuine confirmed booking, usage fees are non-refundable, except in the case of a billing
        error or a successfully raised dispute (see &sect;3).
      </li>
    </ul>

    <h2>3. Billing errors &amp; disputes</h2>
    <p>
      If you believe you have been charged incorrectly &mdash; for example, a booking was billed that the customer did
      not actually attend &mdash; contact us within <strong>{BUSINESS_PROFILE.disputeWindowDays} days</strong> of the
      invoice date. We will review the relevant records and, where a charge is found to be in error, issue a correction
      or refund.
    </p>

    <h2>4. How refunds are issued</h2>
    <p>
      Approved refunds are returned to the original payment method via our payment partner, Razorpay. Refunds are
      typically processed within <strong>{BUSINESS_PROFILE.refundProcessingWindow}</strong> of approval, though the time
      for the amount to reflect in your account depends on your bank or payment provider.
    </p>

    <h2>5. Free tier</h2>
    <p>
      Free-tier usage (within any applicable monthly allowance) is provided at no charge and is therefore not subject to
      refunds.
    </p>

    <h2>6. Contact</h2>
    <p>For cancellations, billing questions, or refund requests:</p>
    <p>
      <strong>
        {BUSINESS_PROFILE.proprietorName} ({BUSINESS_PROFILE.legalEntityLabel})
      </strong>
      <br />
      Email: <a href={mailto()}>{BUSINESS_PROFILE.supportEmail}</a>
      <br />
      Phone: {BUSINESS_PROFILE.supportPhone}
    </p>
  </>
);

const ShippingBody = () => (
  <>
    <p>
      Zopeo provides software and digital services only. We do not sell physical goods and we do not ship physical
      products to Merchants or end customers.
    </p>

    <h2>1. Nature of delivery</h2>
    <p>
      Paid Zopeo Services are delivered digitally through the Zopeo merchant dashboard, website, mobile applications, and
      related account features. Access is provided online after successful account creation, payment confirmation, and
      any required account verification.
    </p>

    <h2>2. Service activation timeline</h2>
    <p>
      For eligible paid accounts, software access is typically activated {BUSINESS_PROFILE.serviceActivationWindow}. Some
      features may require onboarding, configuration, or verification of Merchant business details before they are
      enabled.
    </p>

    <h2>3. No physical shipping charges</h2>
    <p>
      Since Zopeo is a digital software service, there are no physical shipping charges, delivery partners, tracking
      numbers, or shipping addresses required for fulfilment.
    </p>

    <h2>4. Failed activation or access issues</h2>
    <p>
      If payment succeeds but access is not activated within the expected window, contact us using the details below. We
      will verify the payment and account status and either activate the service or advise the next steps.
    </p>

    <h2>5. Contact</h2>
    <p>
      <strong>
        {BUSINESS_PROFILE.proprietorName} ({BUSINESS_PROFILE.legalEntityLabel})
      </strong>
      <br />
      Email: <a href={mailto()}>{BUSINESS_PROFILE.supportEmail}</a>
      <br />
      Phone: {BUSINESS_PROFILE.supportPhone}
    </p>
  </>
);

const ContactBody = () => (
  <>
    <p>
      This page provides official contact, business, support, and escalation details for Zopeo. For sales, onboarding,
      technical support, billing questions, policy requests, or compliance enquiries, please use the channels below.
    </p>

    <h2>Legal business details</h2>
    <p>
      <strong>{BUSINESS_PROFILE.proprietorName}</strong>
      <br />
      {BUSINESS_PROFILE.legalEntityLabel}
      <br />
      Brand name: {BUSINESS_PROFILE.brandName}
      <br />
      Business type: {RAZORPAY_APPLICATION_DETAILS.businessType}
      <br />
      GST status: {BUSINESS_PROFILE.gstStatus}
    </p>

    <h2>Registered / operating address</h2>
    <p>
      {BUSINESS_PROFILE.registeredAddress}
      <br />
      {BUSINESS_PROFILE.cityAndState}, {BUSINESS_PROFILE.country}
    </p>

    <h2>Business overview</h2>
    <p>{RAZORPAY_APPLICATION_DETAILS.description}</p>

    <h2>Business category</h2>
    <p>{RAZORPAY_APPLICATION_DETAILS.category}</p>

    <h2>Customer support</h2>
    <p>
      Email: <a href={mailto()}>{BUSINESS_PROFILE.supportEmail}</a>
      <br />
      Phone: {BUSINESS_PROFILE.supportPhone}
      <br />
      Support hours: {BUSINESS_PROFILE.supportHours}
    </p>

    <h2>Billing and payment support</h2>
    <p>
      For invoice questions, subscription issues, payment confirmation, cancellation requests, or refund requests, email{' '}
      <a href={mailto()}>{BUSINESS_PROFILE.supportEmail}</a> with your registered business name, contact number, invoice
      or payment reference, and a short description of the issue. Approved refunds are processed through Razorpay or the
      applicable payment partner to the original payment method.
    </p>

    <h2>Response timeline</h2>
    <p>
      We aim to acknowledge support and billing requests within 1 business day during support hours. Resolution time
      depends on the nature of the request, account verification, payment partner timelines, and information provided by
      the merchant.
    </p>

    <h2>Demo and sales enquiries</h2>
    <p>
      To request a product walkthrough, onboarding discussion, or merchant plan information, email{' '}
      <a href={mailto()}>{BUSINESS_PROFILE.supportEmail}</a> with your business name, city, business type, and preferred
      contact time.
    </p>

    <h2>Privacy and legal requests</h2>
    <p>
      For privacy, data access, correction, deletion, legal notice, or compliance-related requests, contact us at{' '}
      <a href={mailto()}>{BUSINESS_PROFILE.supportEmail}</a>. Please include enough information for us to verify the
      relevant account or request.
    </p>
  </>
);

// Ordered registry — drives both the sidebar tabs and the route renderer.
export const LEGAL_DOCUMENTS = [
  {
    key: 'privacy',
    path: '/store/privacy',
    label: 'Privacy Policy',
    title: 'Privacy Policy',
    eyebrow: 'Zopeo policy',
    lastUpdated: '29 June 2026',
    Body: PrivacyBody
  },
  {
    key: 'terms',
    path: '/store/terms',
    label: 'Terms & Conditions',
    title: 'Terms & Conditions',
    eyebrow: 'Zopeo policy',
    lastUpdated: '29 June 2026',
    Body: TermsBody
  },
  {
    key: 'refund',
    path: '/store/refund',
    label: 'Cancellation & Refund',
    title: 'Cancellation & Refund Policy',
    eyebrow: 'Zopeo policy',
    lastUpdated: '28 June 2026',
    Body: RefundBody
  },
  {
    key: 'shipping',
    path: '/store/shipping',
    label: 'Shipping & Delivery',
    title: 'Shipping / Digital Delivery Policy',
    eyebrow: 'Zopeo policy',
    lastUpdated: '28 June 2026',
    Body: ShippingBody
  },
  {
    key: 'contact',
    path: '/store/contact',
    label: 'Contact Us',
    title: 'Contact Us',
    eyebrow: 'Business details',
    lastUpdated: '29 June 2026',
    Body: ContactBody
  }
];

export const getLegalDocumentByKey = (key) => LEGAL_DOCUMENTS.find((doc) => doc.key === key) || null;
